import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./OffCatalogueModal.css";

const FALLBACK_CARTOON = {
    id: "off-catalogue-launch",
    title: "A Task for the Next Generation",
    caption: "A task had successfully been created for the next generation.",
    image_path: "/images/off-the-catalogue.webp",
    created_at: "2026-09-27T00:00:00.000Z",
};

const monthFormatter = new Intl.DateTimeFormat("en-ZA", {
    month: "long",
    year: "numeric",
    timeZone: "Africa/Johannesburg",
});

function OffCatalogueModal({ user, onClose, onCountChange }) {
    const [cartoons, setCartoons] = useState([]);
    const [exportingMonth, setExportingMonth] = useState(null);
    const [exportError, setExportError] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState("");
    const [caption, setCaption] = useState("");
    const [image, setImage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");
    const closeButtonRef = useRef(null);

    const canManage = ["admin", "archivist"].includes(String(user?.role || "").toLowerCase());

    useEffect(() => {
        closeButtonRef.current?.focus();
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        let cancelled = false;
        axios.get("/api/resources/off-the-catalogue")
            .then((response) => {
                if (cancelled) return;
                const items = Array.isArray(response.data) && response.data.length ? response.data : [FALLBACK_CARTOON];
                setCartoons(items);
                setSelectedId(items[0].id);
                onCountChange?.(items.length);
            })
            .catch(() => {
                if (cancelled) return;
                setCartoons([FALLBACK_CARTOON]);
                setSelectedId(FALLBACK_CARTOON.id);
                setLoadError("The saved collection could not be reached. Showing the opening cartoon.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [onCountChange]);

    const selected = useMemo(
        () => cartoons.find((cartoon) => cartoon.id === selectedId) || cartoons[0],
        [cartoons, selectedId]
    );

    const panelNumbers = useMemo(() => {
        const chronological = [...cartoons].sort((left, right) =>
            new Date(left.created_at || 0) - new Date(right.created_at || 0)
        );
        return new Map(chronological.map((cartoon, index) => [cartoon.id, index + 1]));
    }, [cartoons]);

    const monthGroups = useMemo(() => {
        const groups = new Map();
        [...cartoons]
            .sort((left, right) => new Date(right.created_at || 0) - new Date(left.created_at || 0))
            .forEach((cartoon) => {
                const date = new Date(cartoon.created_at || Date.now());
                const key = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", timeZone: "Africa/Johannesburg" }).format(date);
                if (!groups.has(key)) {
                    groups.set(key, { key, label: monthFormatter.format(date), cartoons: [] });
                }
                groups.get(key).cartoons.push(cartoon);
            });
        return Array.from(groups.values()).map((group) => ({
            ...group,
            cartoons: group.cartoons.sort(
                (left, right) => (panelNumbers.get(left.id) || 0) - (panelNumbers.get(right.id) || 0)
            ),
        }));
    }, [cartoons, panelNumbers]);

    const yearGroups = useMemo(() => {
        const groups = new Map();
        monthGroups.forEach((month) => {
            const year = month.key.slice(0, 4);
            if (!groups.has(year)) groups.set(year, { year, months: [] });
            groups.get(year).months.push(month);
        });
        return Array.from(groups.values());
    }, [monthGroups]);

    const exportMonth = async (group, perPage = 1) => {
        setExportingMonth(group.key);
        setExportError("");
        try {
            const response = await axios.get(`/api/resources/off-the-catalogue/months/${group.key}/pdf`, { responseType: "blob", params: { perPage } });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Off the Catalogue - ${group.label} - ${perPage} per page.pdf`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            setExportError("Could not export that month. Please try again.");
        } finally { setExportingMonth(null); }
    };

    const beginAdd = () => {
        setEditingId(null);
        setTitle("");
        setCaption("");
        setImage(null);
        setFormError("");
        setShowForm(true);
    };

    const beginEdit = () => {
        if (!selected) return;
        setEditingId(selected.id);
        setTitle(selected.title || "");
        setCaption(selected.caption || "");
        setImage(null);
        setFormError("");
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError("");
        if (!title.trim() || (!editingId && !image)) {
            setFormError(editingId ? "Add a title for this panel." : "Add a title and choose a cartoon image.");
            return;
        }

        const formData = new FormData();
        formData.append("title", title.trim());
        formData.append("caption", caption.trim());
        if (image) formData.append("image", image);

        setSaving(true);
        try {
            const response = editingId
                ? await axios.put(`/api/resources/off-the-catalogue/${editingId}`, formData)
                : await axios.post("/api/resources/off-the-catalogue", formData);
            const next = editingId
                ? cartoons.map((cartoon) => cartoon.id === editingId ? { ...cartoon, ...response.data } : cartoon)
                : [response.data, ...cartoons];
            setCartoons(next);
            setSelectedId(response.data.id);
            onCountChange?.(next.length);
            setTitle("");
            setCaption("");
            setImage(null);
            closeForm();
        } catch (error) {
            setFormError(error.response?.data?.error || "The cartoon could not be saved.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected || cartoons.length <= 1) return;
        if (!window.confirm(`Delete “${selected.title}”? This cannot be undone.`)) return;

        setSaving(true);
        setLoadError("");
        try {
            await axios.delete(`/api/resources/off-the-catalogue/${selected.id}`);
            const next = cartoons.filter((cartoon) => cartoon.id !== selected.id);
            setCartoons(next);
            setSelectedId(next[0]?.id || null);
            onCountChange?.(next.length);
            closeForm();
        } catch (error) {
            setLoadError(error.response?.data?.error || "The cartoon could not be deleted.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="off-catalogue-overlay" role="presentation" onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
        }}>
            <section className="off-catalogue-modal" role="dialog" aria-modal="true" aria-labelledby="off-catalogue-title">
                <header className="off-catalogue-header">
                    <h2 id="off-catalogue-title" className="off-catalogue-accessible-title">Off the Catalogue</h2>
                    <img className="off-catalogue-banner" src="/images/off-catalogue-banner.png" alt="Off the Catalogue — Curious stories from the archives. A Connaught 361 series." />
                    <div className="off-catalogue-header-actions">
                        {canManage && (
                            <button type="button" className="off-catalogue-add" onClick={showForm ? closeForm : beginAdd}>
                                <i className={`fa-solid ${showForm ? "fa-xmark" : "fa-plus"}`} aria-hidden="true" />
                                {showForm ? "Cancel" : "Add cartoon"}
                            </button>
                        )}
                        <button ref={closeButtonRef} type="button" className="off-catalogue-close" onClick={onClose} aria-label="Close Off the Catalogue">
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                        </button>
                    </div>
                </header>

                {showForm && canManage && (
                    <form className="off-catalogue-form" onSubmit={handleSubmit}>
                        <label>
                            Cartoon title
                            <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={140} required />
                        </label>
                        <label>
                            Caption
                            <input value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={300} />
                        </label>
                        <label className="off-catalogue-file">
                            Image
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(event) => setImage(event.target.files?.[0] || null)}
                                required={!editingId}
                            />
                            <span>{image?.name || (editingId ? "Keep current image or choose a replacement" : "Choose a JPG, PNG, or WebP image")}</span>
                        </label>
                        <button type="submit" disabled={saving}>
                            <i className={`fa-solid ${editingId ? "fa-floppy-disk" : "fa-upload"}`} aria-hidden="true" />
                            {saving ? "Saving..." : editingId ? "Save changes" : "Publish panel"}
                        </button>
                        {formError && <p className="off-catalogue-error" role="alert">{formError}</p>}
                    </form>
                )}

                {loadError && <p className="off-catalogue-notice" role="status">{loadError}</p>}

                {exportError && <p role="alert" className="off-catalogue-error">{exportError}</p>}
                <div className="off-catalogue-content">
                    <nav className="off-catalogue-list" aria-label="Cartoon panels">
                        <div className="off-catalogue-list-heading">
                            <strong>Archive</strong>
                            <span>{cartoons.length} {cartoons.length === 1 ? "panel" : "panels"}</span>
                        </div>
                        {loading && <span className="off-catalogue-loading">Loading series...</span>}
                        <div className="off-catalogue-month-scroll">
                            {yearGroups.map((yearGroup, yearIndex) => (
                                <details className="off-catalogue-year" key={yearGroup.year} open={yearIndex === 0}>
                                    <summary><span>{yearGroup.year}</span><small>{yearGroup.months.reduce((total, month) => total + month.cartoons.length, 0)}</small></summary>
                                    {yearGroup.months.map((group, monthIndex) => (
                                        <details className="off-catalogue-month" key={group.key} open={yearIndex === 0 && monthIndex === 0}>
                                            <summary id={`off-catalogue-month-${group.key}`}><span>{group.label.replace(` ${yearGroup.year}`, "")}</span><small>{group.cartoons.length}</small></summary>
                                            <div className="off-catalogue-month-panels">
                                                {canManage && <><button type="button" className="off-catalogue-export" disabled={Boolean(exportingMonth) || Boolean(loadError)} onClick={() => exportMonth(group)} aria-label={`Export ${group.label} as PDF`}>
                                                    {exportingMonth === group.key ? "Creating PDF…" : "Download PDF · 1 per page"}
                                                </button>
                                                <button type="button" className="off-catalogue-export" disabled={Boolean(exportingMonth) || Boolean(loadError)} onClick={() => exportMonth(group, 4)} aria-label={`Export ${group.label} as compact PDF`}>Print PDF · 4 per page</button></>}
                                                {group.cartoons.map((cartoon) => (
                                                    <button
                                                        key={cartoon.id}
                                                        type="button"
                                                        className={cartoon.id === selected?.id ? "selected" : ""}
                                                        onClick={() => setSelectedId(cartoon.id)}
                                                    >
                                                        <img src={cartoon.image_path} alt="" />
                                                        <span><small>Panel {panelNumbers.get(cartoon.id)}</small>{cartoon.title}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </details>
                                    ))}
                                </details>
                            ))}
                        </div>
                    </nav>

                    <article className="off-catalogue-viewer">
                        {selected && (
                            <>
                                <img src={selected.image_path} alt={selected.title} />
                                <footer>
                                    <div>
                                        <span>Off the Catalogue</span>
                                        <h3>{selected.title}</h3>
                                    </div>
                                    {selected.caption && <p>{selected.caption}</p>}
                                    {canManage && (
                                        <div className="off-catalogue-manage">
                                            <button type="button" onClick={beginEdit} disabled={saving}>
                                                <i className="fa-solid fa-pen" aria-hidden="true" /> Edit
                                            </button>
                                            <button type="button" className="danger" onClick={handleDelete} disabled={saving || cartoons.length <= 1}>
                                                <i className="fa-solid fa-trash" aria-hidden="true" /> Delete
                                            </button>
                                        </div>
                                    )}
                                </footer>
                            </>
                        )}
                    </article>
                </div>
            </section>
        </div>
    );
}

export default OffCatalogueModal;
